import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnDestroy } from '@angular/core';

/**
 * A list that drifts upward without end (the reviews at the foot of the themes box, the Web Tools box's
 * converters): the host holds its content twice, and every frame it moves up by its speed, back by half
 * its height once it has gone that far, so the loop is seamless. Driven by script rather than a CSS
 * animation (owner 2026-10-11: the list jumped when the pointer came over it): pausing a CSS transform
 * animation hands it from the compositor back to the page, which snaps it to a slightly different
 * place; here the pointer over the holder (the nearest [data-drift-hold], else the parent) simply stops
 * the drift where it is, and it goes on from there.
 */
@Directive( {
  selector: '[appDrift]',
} )
export class DriftDirective implements AfterViewInit, OnDestroy {
  /** seconds for one lap (the host's half height) */
  @Input() appDrift: number | string = 60;
  /** how far into its lap the list starts, 0..1 (columns side by side start apart) */
  @Input() driftStart = 0;

  private frame = 0;
  private last = 0;
  private offset = -1;
  private held = false;
  private readonly hold = () => ( this.held = true );
  private readonly release = () => ( this.held = false );

  constructor( private el: ElementRef<HTMLElement>, private zone: NgZone ) {}

  ngAfterViewInit(): void {
    if ( typeof window === 'undefined' || window.matchMedia?.( '(prefers-reduced-motion: reduce)' ).matches ) {
      return;
    }
    const parent = this.holder;
    parent?.addEventListener( 'pointerenter', this.hold );
    parent?.addEventListener( 'pointerleave', this.release );
    parent?.addEventListener( 'focusin', this.hold );
    parent?.addEventListener( 'focusout', this.release );
    this.el.nativeElement.style.willChange = 'transform';
    this.zone.runOutsideAngular( () => ( this.frame = requestAnimationFrame( t => this.step( t ) ) ) );
  }

  ngOnDestroy(): void {
    cancelAnimationFrame( this.frame );
    const parent = this.holder;
    parent?.removeEventListener( 'pointerenter', this.hold );
    parent?.removeEventListener( 'pointerleave', this.release );
    parent?.removeEventListener( 'focusin', this.hold );
    parent?.removeEventListener( 'focusout', this.release );
  }

  private get holder(): HTMLElement | null {
    return this.el.nativeElement.closest<HTMLElement>( '[data-drift-hold]' ) || this.el.nativeElement.parentElement;
  }

  private step( now: number ): void {
    const host = this.el.nativeElement;
    const lap = host.offsetHeight / 2;
    const seconds = Math.max( 1, Number( this.appDrift ) || 60 );
    if ( lap > 0 ) {
      if ( this.offset < 0 ) {
        this.offset = lap * Math.min( 1, Math.max( 0, this.driftStart ) );
      }
      // a frame after a pause (or a hidden tab) never leaps: at most a tenth of a second of drift
      const dt = this.last ? Math.min( 0.1, ( now - this.last ) / 1000 ) : 0;
      if ( !this.held ) {
        this.offset = ( this.offset + dt * lap / seconds ) % lap;
      }
      host.style.transform = `translate3d( 0, ${ -this.offset }px, 0 )`;
    }
    this.last = now;
    this.frame = requestAnimationFrame( t => this.step( t ) );
  }
}
